import React, { useMemo, useState } from 'react'
import { DesignSystem, MotifId } from '../types'
import { MOTIFS } from '../data/motifs'
import { Modal } from './Modal'
import { SelectField } from './SelectField'

interface DesignSystemPanelProps {
  systems: DesignSystem[]
  activeSystem: DesignSystem
  onSelect: (id: string) => void
  onCreate: (name: string, motifId: MotifId) => void
  onRename: (name: string) => void
}

export function DesignSystemPanel({
  systems,
  activeSystem,
  onSelect,
  onCreate,
  onRename,
}: DesignSystemPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newName, setNewName] = useState('New System')
  const [newMotif, setNewMotif] = useState<MotifId>('professional')

  const motifOptions = useMemo(
    () =>
      MOTIFS.map((motif) => ({
        id: motif.id,
        name: motif.name,
      })),
    []
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Design Systems
        </h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-md hover:bg-gray-800 cursor-pointer"
        >
          New System
        </button>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-gray-600">System Name</label>
        <input
          value={activeSystem.name}
          onChange={(event) => onRename(event.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
        <p className="text-[11px] text-gray-500">
          Motif: {MOTIFS.find((motif) => motif.id === activeSystem.baseMotifId)?.name ?? 'Custom'}
        </p>
      </div>

      <div className="space-y-2 max-h-52 overflow-auto pr-1">
        {systems.map((system) => {
          const isActive = system.id === activeSystem.id
          const motifName =
            MOTIFS.find((motif) => motif.id === system.baseMotifId)?.name ?? 'Custom'
          return (
            <button
              key={system.id}
              onClick={() => onSelect(system.id)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                isActive
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } cursor-pointer`}
            >
              <div className="text-sm font-semibold">{system.name}</div>
              <div
                className={`text-[11px] ${
                  isActive ? 'text-gray-200' : 'text-gray-500'
                }`}
              >
                {motifName}
              </div>
            </button>
          )
        })}
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New System"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-3 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onCreate(newName.trim() || 'New System', newMotif)
                setIsModalOpen(false)
                setNewName('New System')
                setNewMotif('professional')
              }}
              className="px-4 py-2 text-xs font-semibold text-white bg-gray-900 rounded-md hover:bg-gray-800 cursor-pointer"
            >
              Create System
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-600">Name</label>
            <input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <SelectField<MotifId>
            label="Motif"
            value={newMotif}
            options={motifOptions.map((motif) => ({
              value: motif.id,
              label: motif.name,
            }))}
            onChange={setNewMotif}
          />
        </div>
      </Modal>
    </div>
  )
}
